/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { Text } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';

/**
 * Card chrome every panel on the deal detail screen shares: a white
 * surface lifted off the warm page ground by a hairline border, no
 * shadow.
 *
 * Kept here rather than taken from `SectionCard` only because that
 * primitive takes no `className`, and the deal panels each set their own
 * vertical rhythm. The two now paint the same surface and title
 * themselves the same way.
 *
 * The vertical rhythm is deliberately left to the caller: the tiles, the
 * action bar and the timeline each set their own `gap-*`.
 */
export const DEAL_PANEL_CLASS = cn(
  'flex flex-col rounded-(--radius-lg) border border-(--border-subtle)',
  'bg-(--surface-card) p-(--space-4)',
);

/**
 * The title of a panel ("Propostas", "Contatos", "Linha do tempo").
 *
 * Sentence case, ruled off underneath: the same treatment `SectionCard`
 * gives the property and client panels, so a reader crossing the three
 * screens meets one card. Not all-caps — the extra letter-spacing capitals
 * need only reads as loose without them.
 *
 * @param props.children - The already-localized label text.
 * @param props.accessory - Right-aligned slot on the title row.
 * @returns The panel title row.
 */
export function DealPanelLabel({ children, accessory }: {
  children: React.ReactNode;
  accessory?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-(--space-2) border-b border-(--border-subtle) pb-(--space-3)">
      <Text
        component="span"
        className={cn(
          'text-[length:var(--text-app-section)] [font-weight:var(--font-bold)]',
          'text-(--seed-base)',
        )}
      >
        {children}
      </Text>
      {accessory}
    </div>
  );
}
